// React
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

// Actions
import {
  getYearByIdAction,
  createYearAction,
  patchYearAction,
} from "../../actions/yearActions";

// Multi-device sync layer — same primitives used by DayScreen.
import {
  markDirty,
  flushNow,
  registerSender,
  installUnloadHooks,
  getPending,
  discardFields,
  getOrCreateDeviceId,
} from "../../utils/syncQueue";
import {
  subscribe as socketSubscribe,
  unsubscribe as socketUnsubscribe,
  onPatch as socketOnPatch,
  onReconnect as socketOnReconnect,
  getSocketId,
} from "../../services/socket";
import { YEARS_PATCH_SUCCESS } from "../../constants/yearsConstants";

// Hooks
import { usePersistentLoading } from "../../hooks/usePersistentLoading";
import { useLoadingContent } from "../../hooks/useLoadingContent";
import { useLoadingFloor } from "../../hooks/useLoadingFloor";

// Internal Components
import Header from "../../components/layouts/Header/Header";
import YearDock from "./components/YearDock";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import PageContainer from "../../components/layouts/Containers/PageContainer";
import YearMap from "./components/yearmap/YearMap";
import { LoadingTile } from "../../components/ui/Loading/LoadingTile";

// Utils
import {
  continuationCalculator,
  memorySelector,
} from "../../utils/UtilityFunctions";

// CSS
import "./yearscreen.css";
import SEO from "../../components/seo/SEO";

// --- CUSTOM HOOK: useDebounce ---
// This prevents the save function from firing on every single keystroke.
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export default function YearScreen() {
  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let { id } = useParams();

  const loadingContent = useLoadingContent();
  const pageLoading = useLoadingFloor(2000, loadingContent);
  const { isShowing, startLoading, stopLoading, tileProps } =
    usePersistentLoading(pageLoading, loadingContent);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const yearSingle = useSelector((state) => state.yearSingle?.year);
  const dayList = useSelector((state) => state.dayList);
  const { days, loading } = dayList;

  const isCurrentlyFetching = loading;

  // --- STATE ---
  const [viewWidth, setViewWidth] = useState(1400);

  // Local state for the form data
  const [meyear, setMeyear] = useState(
    yearSingle || {
      birthday: "",
      // Initialize other fields as needed to prevent controlled/uncontrolled errors
    },
  );

  const [pageName, setPageName] = useState(
    id === undefined ? "Create Year" : "Update Year",
  );

  // 'load' controls the visual feedback state (e.g. "Saving...", "Success")
  const [load, setLoad] = useState("");

  // Remote patches that landed for fields the user is currently editing —
  // surfaced via a banner; user opts in to overwrite their typing.
  const [incomingPending, setIncomingPending] = useState({});

  // Use a ref to track if this is the initial mount to prevent autosave on page load
  const isFirstRun = useRef(true);

  // --- DEBOUNCE LOGIC ---
  // We track 'meyear' and wait 1000ms after the last change
  const debouncedYear = useDebounce(meyear, 1500);

  // --- AUTOSAVE EFFECT ---
  // Only handles initial creation. Updates flow through the multi-device
  // sync queue (markDirty in handleChange below); running both at once
  // would have a debounced PUT racing the per-field PATCH and clobbering.
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (!debouncedYear.birthday && !id) return;
    if (id !== undefined) return;

    setLoad("saving");
    dispatch(createYearAction(debouncedYear))
      .then(async (response) => {
        if (response && response._id) {
          navigate(`/year/${response._id}`, { replace: true });
          handleSave();
        }
      })
      .catch((err) => {
        console.error(err);
        setLoad("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedYear]);

  // --- EXISTING EFFECTS ---

  useEffect(() => {
    if (id) dispatch(getYearByIdAction(id));
  }, [id, userInfo, dispatch]);

  useEffect(() => {
    if (isCurrentlyFetching) {
      startLoading();
    } else {
      stopLoading();
    }
  }, [isCurrentlyFetching, startLoading, stopLoading]);

  // Sync Redux state to Local state
  useEffect(() => {
    if (yearSingle) {
      // careful not to overwrite local work-in-progress if syncing happens oddly
      // usually strictly syncing from DB is good, but autosave requires care.
      setMeyear((prev) => ({ ...prev, ...yearSingle }));

      setMeyear((prevYear) => {
        let shallow = { ...prevYear };
        if (days) {
          shallow["memory"] = memorySelector(days);
        }
        return shallow;
      });
    }
  }, [yearSingle, days]);

  useEffect(() => {
    if (meyear.birthday) {
      let birthday = meyear.birthday;
      setMeyear((prevYear) => {
        let shallow = { ...prevYear };
        shallow["continuation"] = continuationCalculator(birthday);
        return shallow;
      });
    }
  }, [meyear.birthday]);

  // Register the year sender once. patchYearAction carries the
  // server-assigned socketId so the broadcast layer can suppress this
  // device's own echo.
  useEffect(() => {
    registerSender("year", async (recordId, payload) => {
      try {
        const enriched = { ...payload, socketId: getSocketId() };
        const response = await dispatch(patchYearAction(recordId, enriched));
        handleSave();
        return response;
      } catch (err) {
        setLoad("error");
        throw err;
      }
    });
    installUnloadHooks(process.env.REACT_APP_API_URL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Flush pending edits when the user navigates away from this year.
  useEffect(() => {
    if (!id) return undefined;
    return () => {
      flushNow("year", id).catch(() => {});
    };
  }, [id]);

  // Subscribe to live broadcasts for the current year. Non-dirty fields
  // update silently; fields the user is currently editing are queued in
  // incomingPending and surface via the banner.
  useEffect(() => {
    if (!id) return undefined;
    socketSubscribe("year", id);

    const offPatch = socketOnPatch((msg) => {
      if (!msg || msg.type !== "year" || msg.id !== id) return;
      if (msg.originDeviceId && msg.originDeviceId === getOrCreateDeviceId())
        return;
      const incoming = msg.patches || {};
      const dirty = getPending("year", id);
      const conflicting = {};

      setMeyear((prev) => {
        const next = { ...prev };
        for (const [field, value] of Object.entries(incoming)) {
          if (dirty[field]) {
            conflicting[field] = value;
          } else {
            next[field] = value;
          }
        }
        return next;
      });

      if (Object.keys(conflicting).length > 0) {
        setIncomingPending((prev) => ({ ...prev, ...conflicting }));
      }

      if (typeof msg.version === "number" || msg.fieldMeta) {
        dispatch({
          type: YEARS_PATCH_SUCCESS,
          payload: { version: msg.version, fieldMeta: msg.fieldMeta },
        });
      }
    });

    const offReconnect = socketOnReconnect(() => {
      dispatch(getYearByIdAction(id));
    });

    return () => {
      offPatch();
      offReconnect();
      socketUnsubscribe("year", id);
    };
  }, [id, dispatch]);

  const acceptIncomingChanges = () => {
    if (Object.keys(incomingPending).length === 0) return;
    const fields = Object.keys(incomingPending);
    setMeyear((prev) => ({ ...prev, ...incomingPending }));
    discardFields("year", id, fields);
    setIncomingPending({});
  };

  // --- HANDLERS ---

  const handleSubmitRef = useRef(null);
  useEffect(() => {
    const onNavSave = () => handleSubmitRef.current?.();
    window.addEventListener("nwd:save", onNavSave);
    return () => window.removeEventListener("nwd:save", onNavSave);
  }, []);

  // Manual submit (Backup for the button)
  const handleSubmit = async () => {
    setLoad("saving");
    if (!id) {
      dispatch(createYearAction(meyear)).then((res) => {
        if (res && res._id) navigate(`/year/${res._id}`);
      });
    } else {
      // Flush any debounced field edits immediately. The PATCH carries
      // only what was queued via markDirty — clicking "Save" should not
      // resurrect fields the user previously deleted from the form.
      flushNow("year", id).catch(() => {});
    }
  };
  handleSubmitRef.current = handleSubmit;

  const handleChange = (e, tile_name) => {
    const { name, value, title } = e.target;
    if (!tile_name) return;

    // Compute the new top-level value first so we can both setMeyear with
    // a flat shape and pass the same value to markDirty without doing the
    // computation twice.
    let nextTopValue;
    if (tile_name.startsWith("v")) {
      nextTopValue = {
        ...(meyear[tile_name] || {}),
        [name]: {
          ...(meyear[tile_name]?.[name] || {}),
          [title]: value,
        },
      };
    } else {
      nextTopValue = value;
    }

    setMeyear((prev) => ({ ...prev, [tile_name]: nextTopValue }));

    // Mark the whole top-level field dirty — mergeBlob's per-field LWW
    // operates on top-level keys, so concurrent edits to v1.foo.bar and
    // v2.baz.qux are independent, but two devices both touching v1 will
    // race at the v1 level.
    if (id) markDirty("year", id, tile_name, nextTopValue);
  };

  const handleSave = () => {
    setLoad("success");
    setTimeout(() => {
      setLoad("");
    }, 2000);
  };

  const hasIncoming = Object.keys(incomingPending).length > 0;

  return (
    <>
      {hasIncoming && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            background: "rgba(0, 74, 35, 0.95)",
            color: "white",
            padding: "10px 16px",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>Updated on another device</span>
          <button
            type="button"
            onClick={acceptIncomingChanges}
            style={{
              background: "white",
              color: "rgb(0, 74, 35)",
              border: "none",
              padding: "4px 10px",
              borderRadius: 4,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Apply
          </button>
        </div>
      )}
      <SEO
        title={pageName}
        description="Create and update your year map"
        canonicalUrl={"https://nowasteddays.io/week/year"}
      />
      <Header loading={load} variant="light" active={true} />

      {/* Forms & Inputs Guidelines[cite: 38]:
         Inputs should have transparent backgrounds and Primary text color[cite: 41].
      */}
      <form onSubmit={(e) => e.preventDefault()}>
        <PageContainer className="yearContainer">
          {isShowing ? (
            <LoadingTile content={loadingContent} {...tileProps} />
          ) : (
            <>
              <YearMap year={meyear} handleChange={handleChange} />
            </>
          )}
        </PageContainer>
      </form>
      <TestFooter />
    </>
  );
}
